import anime from 'animejs/lib/anime.es.js';

export function animateStagger(element, options = {}) {
    const {
        duration = 800,
        delay = 200,
        startDelay = 0,
        complete = () => {}
    } = options;

    const panels = element.querySelectorAll('.panel');
    
    // Reset panels to initial state
    panels.forEach(panel => {
        panel.style.opacity = '0';
        panel.style.transform = 'translateY(20px)';
    });

    // Make panels container visible
    element.style.opacity = '1';

    // Animate each panel with stagger
    setTimeout(() => {
        anime({
            targets: panels,
            opacity: [0, 1],
            translateY: [20, 0],
            duration: duration,
            easing: 'easeOutQuad',
            delay: anime.stagger(delay),
            complete: () => {
                // Ensure final state is set
                panels.forEach(panel => {
                    panel.style.opacity = '1';
                    panel.style.transform = 'translateY(0)';
                });
                
                // Call complete callback after animation and final state
                complete();
            }
        });
    }, startDelay);
}

export function setupSpinning(map) {
    if (!map) {
        console.error("Map object is not available for setupSpinning");
        return { startSpinning: () => {}, stopSpinning: () => {} };
    }

    const secondsPerRevolution = 400;
    const minSpinZoom = 0;
    const maxSpinZoom = 4;
    let userInteracting = false;
    let spinEnabled = true;
    let spinningAnimationFrame = null;
    let lastSpinTime = 0;
    let isFlying = false;

    function spinGlobe(timestamp) {
        if (spinEnabled && !userInteracting && !isFlying && map.isStyleLoaded()) {
            const zoom = map.getZoom();
            if (zoom >= minSpinZoom && zoom < maxSpinZoom) {
                const now = Date.now();
                if (now - lastSpinTime > 16) { // Limit to roughly 60fps
                    const center = map.getCenter();
                    center.lng -= 360 / secondsPerRevolution / 60;
                    map.easeTo({ center, duration: 0, animate: false });
                    lastSpinTime = now;
                }
            }
        }
        spinningAnimationFrame = requestAnimationFrame(spinGlobe);
    }

    function startSpinning() {
        if (!spinningAnimationFrame && !isFlying) {
            spinningAnimationFrame = requestAnimationFrame(spinGlobe);
        }
    }

    function stopSpinning() {
        if (spinningAnimationFrame) {
            cancelAnimationFrame(spinningAnimationFrame);
            spinningAnimationFrame = null;
        }
    }

    map.on('mousedown', () => {
        userInteracting = true;
        stopSpinning();
    });

    map.on('touchstart', () => {
        userInteracting = true;
        stopSpinning();
    });

    map.on('wheel', () => {
        userInteracting = true;
        stopSpinning();
    });

    map.on('mouseup', () => {
        userInteracting = false;
    });

    map.on('dragend', () => {
        userInteracting = false;
        if (!isFlying) setTimeout(startSpinning, 1000);
    });

    map.on('zoomend', () => {
        userInteracting = false;
        if (!isFlying) setTimeout(startSpinning, 1000);
    });

    map.on('pitchend', () => {
        userInteracting = false;
        if (!isFlying) setTimeout(startSpinning, 1000);
    });

    map.on('rotateend', () => {
        userInteracting = false;
        if (!isFlying) setTimeout(startSpinning, 1000);
    });

    map.on('moveend', () => {
        userInteracting = false;
        if (map.isStyleLoaded() && !isFlying) {
            setTimeout(startSpinning, 1000);
        }
    });

    map.on('style.load', () => {
        if (!isFlying) startSpinning();
    });

    // Start flying state management
    map.on('flystart', () => {
        isFlying = true;
        stopSpinning();
    });

    map.on('flyend', () => {
        isFlying = false;
        if (map.isStyleLoaded()) {
            setTimeout(startSpinning, 1000);
        }
    });

    startSpinning();

    return {
        startSpinning,
        stopSpinning,
        setFlying: (state) => { isFlying = state; }
    };
}